import React, { useContext, useEffect, useRef, useState } from 'react';
import ArsModelDetails from './ArsModelDetails';
import { DEFAULT_SUBMODEL, TARGET_CATEGORIES } from '../ARSConstants';
import CreateSubmodelDialog from './CreateSubmodelDialog';
import { createRecommenderSubmodel, updateRecommenderModel } from '../ARSServices/ARSServices';
import { toast } from 'react-toastify';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import OverflowOption from './OverflowOption';
import NoDataComponent from 'Components/Common/NoDataComponent';
import DefaultSubmodelBaseSelector from './DefaultSubmodelBaseSelector';
import TargetCategorySelector from './TargetCategorySelector';
import { ArsContext } from '../Contexts/ArsContext';
import BaseCategorySelector from './BaseCategorySelector';
import { RecommenderLogsResponse, TSetPriorityMode, TSubmodel } from '../Types';

type TDefineModel = {
    model: RecommenderLogsResponse | null,
    setModel: React.Dispatch<React.SetStateAction<RecommenderLogsResponse | null>>
    updateSetModePriority: (setModePriority: TSetPriorityMode[], submodelId: string) => void;
}

const ArsDefineModel = ({ model, setModel, updateSetModePriority }: TDefineModel) => {

    const [isTargetCategoriesEnabled, setIsTargetCategoriesEnabled] = useState<boolean>(false);
    const [isExpertCuratedEnabled, setIsExpertCuratedEnabled] = useState<boolean>(false);

    const [isCreateSubmodelDialogOpen, setIsCreateSubmodelDialogOpen] = useState<boolean>(false);
    const [creatingSubmodel, setCreatingSubmodel] = useState<boolean>(false);

    const [menuOpen, setMenuOpen] = useState<string | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [submodelToRename, setSubmodelToRename] = useState<TSubmodel | null>(null);
    const { allCategories, activeSubmodelIndex, setActiveSubmodelIndex } = useContext(ArsContext);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const openDialog = () => setIsCreateSubmodelDialogOpen(true);
    const closeDialog = () => setIsCreateSubmodelDialogOpen(false);

    useEffect(() => {
        if (!model?.submodel?.[activeSubmodelIndex]) return;
        const activeSubmodel = model.submodel[activeSubmodelIndex];

        setIsTargetCategoriesEnabled(activeSubmodel.target_categories?.length > 0 || false);

        const expertCuratedMode = activeSubmodel.setModePriority?.find(
            mode => mode.mode === 'expert_curated_coseller' && mode.active
        );
        setIsExpertCuratedEnabled(!!expertCuratedMode);
    }, [model, activeSubmodelIndex]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(null);
            }
        }
        function handleScroll() {
            if (setMenuOpen != null) {
                setMenuOpen(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [menuOpen]);

    const handleMenuToggle = (e: React.MouseEvent, submodelId: string) => {
        e.stopPropagation();
        setMenuOpen(menuOpen === submodelId ? null : submodelId);
    };

    const handleRename = (e: React.MouseEvent, submodel: TSubmodel) => {
        e.stopPropagation();
        setMenuOpen(null);
        setSubmodelToRename(submodel);
        setIsRenaming(true);
    };

    const handleDelete = async (e: React.MouseEvent, submodelId: string) => {
        e.stopPropagation();
        setMenuOpen(null);

        if (!model) return;

        try {
            const updatedSubmodels = model.submodel?.filter(
                submodel => submodel.submodel_id !== submodelId
            ) || [];

            const updatedModel = {
                ...model,
                submodel: updatedSubmodels
            } as RecommenderLogsResponse;

            setModel(updatedModel);
            if (updatedSubmodels.length <= activeSubmodelIndex) {
                setActiveSubmodelIndex(Math.max(0, updatedSubmodels.length - 1));
            }
        } catch (error) {
            console.error("Error deleting submodel:", error);
            toast.error("Failed to delete submodel. Please try again.");
        }
    };

    const confirmRename = async (newName: string) => {
        if (!newName || !newName.trim() || !submodelToRename || !model) {
            setIsRenaming(false);
            return;
        }

        try {
            const updatedSubmodels = model.submodel?.map(submodel =>
                submodel.submodel_id === submodelToRename.submodel_id
                    ? { ...submodel, name: newName.trim() }
                    : submodel
            ) || [];

            const updatedModel = {
                ...model,
                submodel: updatedSubmodels
            } as RecommenderLogsResponse;
            setModel(updatedModel);
        } catch (error) {
            console.error("Error renaming submodel:", error);
            toast.error("Failed to rename submodel. Please try again.");
        } finally {
            setIsRenaming(false);
            setSubmodelToRename(null);
        }
    };

    const createSubmodel = async (name: string) => {
        if (!model?.model_id) return;

        setCreatingSubmodel(true);

        try {
            const response = await createRecommenderSubmodel(model?.model_id, { name });
            if (response?.done) {
                setModel(response.data);
                setActiveSubmodelIndex(model?.submodel?.length ?? 0);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to create submodel");
        } finally {
            setCreatingSubmodel(false);
            closeDialog();
        }
    };

    const handleSubmodelSelect = (index: number) => {
        setActiveSubmodelIndex(index);
    };

    const handleCreateClick = async () => {
        if (!model) return;

        try {
            const updateModelResponse = await updateRecommenderModel(model);

            if (!updateModelResponse?.done) {
                toast.error(updateModelResponse?.message ?? "Failed to update model");
                return;
            }

            openDialog();
        } catch (error) {
            console.log(error);
            toast.error("Failed to update model");
        }
    };

    const handleTargetCategoriesToggle = (isEnabled: boolean) => {
        setIsTargetCategoriesEnabled(isEnabled);

        if (isEnabled && isExpertCuratedEnabled) {
            toggleExpertCurated(false);
        }
    };

    const handleExpertCuratedToggle = (isEnabled: boolean) => {
        setIsExpertCuratedEnabled(isEnabled);

        if (isEnabled && isTargetCategoriesEnabled) {
            if (model && activeSubmodelIndex !== undefined) {
                const submodelId = model.submodel[activeSubmodelIndex]?.submodel_id;
                if (submodelId) {
                    setModel(prevModel => {
                        if (!prevModel) return null;

                        const updatedSubmodels = [...prevModel.submodel];
                        updatedSubmodels[activeSubmodelIndex] = {
                            ...updatedSubmodels[activeSubmodelIndex],
                            target_categories: []
                        };

                        return {
                            ...prevModel,
                            submodel: updatedSubmodels
                        };
                    });
                    setIsTargetCategoriesEnabled(false);
                }
            }
        }
    };

    const toggleExpertCurated = (isEnabled: boolean) => {
        if (!model || activeSubmodelIndex === undefined) return;

        const activeSubmodel = model.submodel[activeSubmodelIndex];
        if (!activeSubmodel || !activeSubmodel.setModePriority) return;

        const expertCuratedIndex = activeSubmodel.setModePriority.findIndex(
            mode => mode.mode === 'expert_curated_coseller'
        );

        if (expertCuratedIndex === -1) return;

        const updatedPriority = [...activeSubmodel.setModePriority];
        updatedPriority[expertCuratedIndex] = {
            ...updatedPriority[expertCuratedIndex],
            active: isEnabled
        };

        updateSetModePriority(updatedPriority, activeSubmodel.submodel_id);
    };

    const activeSubmodel = model?.submodel[activeSubmodelIndex];

    return (
        <React.Fragment>
            <div className="tw-flex tw-h-[calc(100vh-171px)]">
                <div className="tw-flex-grow tw-w-[27%] tw-max-h-full tw-py-2.5  tw-border-r-4 tw-border tw-border-[#dadbdd] tw-overflow-y-auto">
                    <div className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2'>
                        <label className='tw-text-[#090A42] tw-font-semibold tw-text-sm tw-ps-2'>Submodel</label>
                        <button onClick={handleCreateClick} data-property-1="Default" className="tw-h-6 tw-px-3 tw-py-2 tw-mr-2 tw-bg-indigo-50 tw-rounded tw-inline-flex tw-justify-center tw-items-center tw-gap-2.5">
                            <div className="tw-text-center tw-justify-start tw-text-blue-800 tw-text-xs">Create </div>
                        </button>
                    </div>
                    <hr className="tw-border-t tw-border-[#dadbdd]" />
                    {!model?.submodel?.length ?
                        <NoDataComponent title='No submodel available' message="Click 'Create' to add your first command submodel." /> :
                        model?.submodel?.map((submodel, index) => (
                            <div
                                key={submodel?.submodel_id + index}
                                className={`tw-w-[100%] tw-h-11 tw-p-2.5 tw-bg-[#f0f3fe] tw-border tw-border-[#091caa] tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex tw-cursor-pointer ${index === activeSubmodelIndex
                                    ? "tw-bg-[#f0f3fe] tw-border-[#091caa]"
                                    : "tw-bg-white tw-border-[#dadbdd]"
                                    }`}
                                onClick={() => handleSubmodelSelect(index)}
                            >
                                <div className="tw-grow tw-shrink tw-basis-0 tw-text-[#172b4d] tw-text-sm tw-leading-tight">
                                    {submodel?.name ?? DEFAULT_SUBMODEL}
                                </div>

                                {submodel?.name && submodel?.name !== DEFAULT_SUBMODEL && <div className='tw-relative'> <button
                                    className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100"
                                    onClick={(e) => handleMenuToggle(e, submodel.submodel_id)}
                                ><OverFlowIcon /> </button>
                                    {menuOpen === submodel.submodel_id && (
                                        <div ref={menuRef} className="tw-relative tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]" >
                                            <div className={`tw-origin-top-left tw-right-0 tw-absolute tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                {OverflowOption(submodel, <EditIcon />, "Rename", (e) => handleRename(e, submodel), true)}
                                                {OverflowOption(submodel, <DeleteIcon />, "Delete", (e) => handleDelete(e, submodel.submodel_id), true)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                }
                            </div>
                        ))}
                </div>
                <div className="tw-flex-grow tw-w-[80%] tw-max-h-full  tw-overflow-y-auto tw-bg-white">
                    {model?.submodel?.length ? <div>
                        <div className="tw-w-[100%] tw-h-11 tw-p-2.5 tw-bg-white tw-border tw-border-[#dadbdd] tw-items-center tw-gap-2.5 tw-flex tw-justify-between">
                            <div className="tw-text-[#172b4d] tw-text-sm tw-leading-tight">
                                {activeSubmodel?.name ?? DEFAULT_SUBMODEL}
                            </div>
                        </div>
                        {activeSubmodel && activeSubmodel?.name === DEFAULT_SUBMODEL ? <DefaultSubmodelBaseSelector submodel={activeSubmodel} key={activeSubmodel.submodel_id + 'source-default'} /> : null}
                        {activeSubmodel && activeSubmodel?.name !== DEFAULT_SUBMODEL ? <BaseCategorySelector submodel={activeSubmodel} key={activeSubmodel.submodel_id + 'source'} /> : null}
                        {activeSubmodel && model?.type === "Co-seller" && (<TargetCategorySelector title={TARGET_CATEGORIES} categories={allCategories} selectedCategories={allCategories.filter(category => activeSubmodel?.target_categories?.includes(category.id))} submodel={activeSubmodel} key={activeSubmodel.submodel_id + 'target'} onToggleChange={handleTargetCategoriesToggle} />)}
                        {activeSubmodel && activeSubmodel.setModePriority && (<ArsModelDetails setModePriority={activeSubmodel.setModePriority} updateSetModePriority={(setModePriority) => { updateSetModePriority(setModePriority, activeSubmodel?.submodel_id) }} activeSubmodel={activeSubmodel} isDisabled={isTargetCategoriesEnabled} onExpertCuratedToggle={handleExpertCuratedToggle} />)}
                    </div> : null}
                </div>
            </div>

            {isCreateSubmodelDialogOpen && <CreateSubmodelDialog
                isOpen={isCreateSubmodelDialogOpen}
                id={`create-submodel`}
                dialogHeader={"Create Submodel"}
                labelName={"Submodel name"}
                onClose={closeDialog}
                onFieldSubmit={createSubmodel}
                showIndicator={creatingSubmodel}
            />}
            {isRenaming && (
                <CreateSubmodelDialog
                    isOpen={isRenaming}
                    id={`rename-submodel`}
                    dialogHeader={"Rename Submodel"}
                    labelName={"Submodel name"}
                    onClose={() => setIsRenaming(false)}
                    onFieldSubmit={confirmRename}
                    showIndicator={false}
                    actionLabel={"Rename"}
                    value={submodelToRename?.name ?? ''}
                />
            )}
        </React.Fragment>
    );
}

export default ArsDefineModel;